// Basic
import { useState } from 'react';
// Store & actions
import useSessionStore from '@/app/store/session';
import useEnvelopesStore from '@/app/store/modules/envelopes';
// Components
import EnvelopesTable from '@/app/modules/envelopes/components/tables/EnvelopesTable';
import ModalDeprogramEnvelopes from '@/app/modules/envelopes/components/modals/deprogramEnvelopes/ModalDeprogramEnvelopes';
import ModalCreateOrUpdateDownloadCode from '@/app/modules/envelopes/components/modals/createOrUpdateDownloadCode/ModalCreateOrUpdateDownloadCode';
import ModalCreateEnvelopeByForm from '@/app/modules/envelopes/components/modals/createEnvelopeByForm/ModalCreateEnvelopeByForm';
import { Tabs, TabsTrigger, TabsList, TabsContent } from '@/common-utils/components/ui/tabs';
import Page from '@/common-utils/components/structure/page/Page';
import DownloadCodesTable from '@/app/modules/envelopes/components/tables/DownloadCodesTable';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import Entity from '@/common-utils/components/renders/entity/Entity';
import ModalImport from '@/common-utils/components/renders/dataTable/import/ModalImport';
import EnvelopeImportProps from '@/app/modules/envelopes/components/import/EnvelopeImportProps';
// Common
import RouteUrls from '../enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import ENVELOPES_TABS from '../enums/tabs/EnvelopesTabs';
import useTabManager from '../hooks/useTabManager';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsEnvelope } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} Envelopes
 */
function Envelopes() {

    // URL Params
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = extractQueriesFromUrl(location.search);

    // Store
    const { permissions: userPermissions } = useSessionStore(s => s.session);
    const storeRefetchEnvelopes = useEnvelopesStore(s => s.storeRefetchEnvelopes);

    // Constants
    const hasCreatePermission = hasRequiredPermissions({
        permissions: userPermissions,
        requiredPermissions: [
            Permissions.CSV_INSERT_POS_REMOTE_DOWNLOAD.name
        ]
    });

    // State
    const [showModalCreateEnvelopeByForm, setShowModalCreateEnvelopeByForm] = useState(false);
    const [showModalCreateEnvelopeByFile, setShowModalCreateEnvelopeByFile] = useState(false);
    const [showModalCreateDownloadCode, setShowModalCreateDownloadCode] = useState(false);
    const [showModalDeprogramEnvelopes, setShowModalDeprogramEnvelopes] = useState(false);
    const [activeTab, setActiveTab] = useState(Object.values(ENVELOPES_TABS).find(tab => tab.name === view)?.name ?? ENVELOPES_TABS.DEFAULT.name);

    // Hooks
    useTabManager({
        pathname: location.pathname,
        view: view,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: ENVELOPES_TABS,
        navigate: navigate
    });

    return (
        <Page>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <Page.Header route={RouteUrls.ENVELOPES} routeBase={RouteUrls.ENVELOPES}>
                    <TabsList>
                        <TabsTrigger tab={ENVELOPES_TABS.ENVELOPES} activeTab={activeTab} permissions={userPermissions}>
                            <Entity entity='page.envelopes.tabs.envelopes' />
                        </TabsTrigger>
                        <TabsTrigger tab={ENVELOPES_TABS.DOWNLOAD_CODES} activeTab={activeTab} permissions={userPermissions}>
                            <Entity entity='page.envelopes.tabs.downloadCodes' />
                        </TabsTrigger>
                    </TabsList>
                </Page.Header>
                <Page.Body dataCy='page-envelopes'>
                    <TabsContent tab={ENVELOPES_TABS.ENVELOPES} permissions={userPermissions}>

                        <EnvelopesTable
                            filtersEventName={setFiltersEventName('envelopes')}
                            pageActions={
                                <PageActions actions={
                                    [
                                        {
                                            isDropdown: true,
                                            visible: true,
                                            trigger: {
                                                icon: ICONS.ADD_ENVELOPES,
                                                entity: 'actions.page.create_envelopes.title',
                                                namespace: nsEnvelope,
                                                visible: true,
                                                disabled: !hasCreatePermission,
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            subActions: [
                                                {
                                                    entity: 'actions.page.create_envelopes.select',
                                                    namespace: nsEnvelope,
                                                    onClick: () => setShowModalCreateEnvelopeByForm(true),
                                                    visible: true,
                                                    disabled: !hasCreatePermission,
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                },
                                                {
                                                    entity: 'actions.page.create_envelopes.upload',
                                                    namespace: nsEnvelope,
                                                    onClick: () => setShowModalCreateEnvelopeByFile(true),
                                                    visible: true,
                                                    disabled: !hasCreatePermission,
                                                    tooltipForbidden: 'title.permissions.access_denied'
                                                }
                                            ]
                                        },
                                        {
                                            icon: ICONS.CLOSE,
                                            entity: 'actions.page.deprogram',
                                            namespace: nsEnvelope,
                                            variant: 'critical',
                                            onClick: () => setShowModalDeprogramEnvelopes(true),
                                            visible: true,
                                            disabled: !hasRequiredPermissions({
                                                permissions: userPermissions,
                                                requiredPermissions: [
                                                    Permissions.DELETE_REMOTE_DOWNLOADS.name
                                                ]
                                            }),
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        }
                                    ]}
                                    retractable
                                />
                            } />

                    </TabsContent>
                    <TabsContent tab={ENVELOPES_TABS.DOWNLOAD_CODES} permissions={userPermissions}>
                        <DownloadCodesTable
                            filtersEventName={setFiltersEventName('download_codes')}
                            pageActions={
                                <PageActions actions={
                                    [
                                        {
                                            icon: ICONS.ADD_DOWNLOAD_CODES,
                                            entity: 'actions.page.create_download_code',
                                            namespace: nsEnvelope,
                                            onClick: () => setShowModalCreateDownloadCode(true),
                                            visible: true,
                                            disabled: !hasCreatePermission,
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        }
                                    ]}
                                    retractable
                                />
                            }
                        />

                    </TabsContent>
                </Page.Body>
            </Tabs>
            {showModalCreateEnvelopeByForm &&
                <ModalCreateEnvelopeByForm
                    isOpen={showModalCreateEnvelopeByForm}
                    setIsOpen={setShowModalCreateEnvelopeByForm}
                />
            }
            {showModalCreateEnvelopeByFile &&
                <ModalImport
                    isOpen={showModalCreateEnvelopeByFile}
                    setIsOpen={setShowModalCreateEnvelopeByFile}
                    importProps={{ ...EnvelopeImportProps, setLaunchRefetch: storeRefetchEnvelopes }}
                />
            }
            {showModalCreateDownloadCode &&
                <ModalCreateOrUpdateDownloadCode
                    isOpen={showModalCreateDownloadCode}
                    setIsOpen={setShowModalCreateDownloadCode}
                />
            }
            {showModalDeprogramEnvelopes &&
                <ModalDeprogramEnvelopes
                    isOpen={showModalDeprogramEnvelopes}
                    setIsOpen={setShowModalDeprogramEnvelopes}
                />
            }
        </Page>
    );
}

export default Envelopes;